import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';

import { DialogModule } from 'primeng/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { YoutubeService } from '../../../services/youtube/youtube.service';
import { YoutubePlaylistResponse } from '../../../models/youtube/youtubePlaylistResponse';
import { YoutubePlaylistItemsResponse } from '../../../models/youtube/youtubePlaylistItemsResponse';

import { MessageService } from 'primeng/api';
@Component({
  selector: 'app-vlogs',
  standalone: true,
  imports: [
    CommonModule, 
    SkeletonModule, 
    ButtonModule, 
    CardModule,
    DialogModule
  ],
  templateUrl: './vlogs.component.html',
  styleUrls: ['./vlogs.component.scss']
})
export class VlogsComponent implements OnInit, AfterViewInit, OnDestroy {
  private youtubeService = inject(YoutubeService);
  private messageService = inject(MessageService);
  private sanitizer = inject(DomSanitizer);

  // --- Video Popup Dialog ---
  displayVideoDialog: boolean = false;
  safeVideoUrl: SafeResourceUrl | null = null;
  activeVideoTitle: string = '';

  openVideo(video: YoutubePlaylistItemsResponse) {
    const videoId = video.videoId;
    if (videoId) {
      this.activeVideoTitle = video.title || 'Watch Video';
      this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}?autoplay=1`);
      this.displayVideoDialog = true;
    }
  }

  onCloseVideo() {
    this.safeVideoUrl = null;
    this.activeVideoTitle = '';
  }

  // --- Constants ---
  // The "All Videos" dummy playlist object
  readonly allVideosOption: YoutubePlaylistResponse = { 
    id: 'ALL_VIDEOS_ID', 
    title: 'All Videos',
    description: 'All available videos'
  };

  // --- Data Signals ---
  playlists = signal<YoutubePlaylistResponse[]>([]);
  videos = signal<YoutubePlaylistItemsResponse[]>([]);
  selectedPlaylist = signal<YoutubePlaylistResponse>(this.allVideosOption);

  // --- Loading Signals ---
  isLoadingPlaylists = signal<boolean>(true);     // Initial Playlist Load
  isLoadingMorePlaylists = signal<boolean>(false); // Infinite Scroll Playlist
  
  isLoadingVideos = signal<boolean>(false);        // Initial Video Load
  isLoadingMoreVideos = signal<boolean>(false);    // Infinite Scroll Video

  // --- Pagination Tokens ---
  private playlistNextToken?: string;
  public videoNextToken?: string;

  // --- Infinite Scroll Elements ---
  @ViewChild('videoScrollAnchor') videoScrollAnchor!: ElementRef;
  @ViewChild('playlistScrollAnchor') playlistScrollAnchor!: ElementRef;
  
  private videoObserver?: IntersectionObserver;
  private playlistObserver?: IntersectionObserver;

  readonly skeletonItems = Array(6).fill(0);

  ngOnInit() {
    // 1. Load the list of playlists (Sidebar)
    this.loadPlaylists();

    // 2. Load "All Videos" by default (Main Grid)
    this.onPlaylistSelect(this.allVideosOption);
  }

  ngAfterViewInit() {
    this.setupVideoScroll();
    this.setupPlaylistScroll();
  }

  ngOnDestroy() {
    this.videoObserver?.disconnect();
    this.playlistObserver?.disconnect();
  }

  // ==========================================
  // 1. Playlist Logic (Left Sidebar)
  // ==========================================

  loadPlaylists(isMore: boolean = false) {
    if (isMore) this.isLoadingMorePlaylists.set(true);
    else this.isLoadingPlaylists.set(true);

    const token = isMore ? this.playlistNextToken : undefined;

    this.youtubeService.getPlaylists(token).subscribe({
      next: (res) => {
        if (isMore) {
          this.playlists.update(curr => [...curr, ...res.items]);
        } else {
          this.playlists.set([this.allVideosOption, ...res.items]);
          // Auto-select first if available
          // if (res.items.length > 0) this.onPlaylistSelect(res.items[0]);
        }
        
        this.playlistNextToken = res.nextPageToken;
        this.isLoadingPlaylists.set(false);
        this.isLoadingMorePlaylists.set(false);
      },
      error: (error) => {
        this.isLoadingPlaylists.set(false);
        this.isLoadingMorePlaylists.set(false);
        this.messageService.add({ severity: 'error', summary: error.name, detail: error.message });
      }
    });
  }

  onPlaylistSelect(playlist: YoutubePlaylistResponse) {
    this.selectedPlaylist.set(playlist);
    this.videos.set([]);
    this.videoNextToken = undefined;
    this.isLoadingVideos.set(true);
    this.fetchVideos(playlist.id);
  }

  // --- Playlist Infinite Scroll Setup ---
  private setupPlaylistScroll() {
    this.playlistObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.playlistNextToken && !this.isLoadingMorePlaylists()) {
          this.loadPlaylists(true);
        }
      });
    }, { root: null, threshold: 0.1 });

    if (this.playlistScrollAnchor) {
      this.playlistObserver.observe(this.playlistScrollAnchor.nativeElement);
    }
  }


  // ==========================================
  // 2. Video Logic (Right Grid)
  // ==========================================

  fetchVideos(playlistId: string, isMore: boolean = false) {
    if (isMore) this.isLoadingMoreVideos.set(true);

    const token = isMore ? this.videoNextToken : undefined;
    if(playlistId === this.allVideosOption.id) {
      this.youtubeService.getVideos(token).subscribe({
        next: (res) => {
          if (isMore) {
            this.videos.update(curr => [...curr, ...res.items]);
          } else {
            this.videos.set(res.items);
          }
          
          this.videoNextToken = res.nextPageToken;
          this.isLoadingVideos.set(false);
          this.isLoadingMoreVideos.set(false);
        },
        error: (error) => {
          this.isLoadingVideos.set(false);
          this.isLoadingMoreVideos.set(false);
          this.messageService.add({ severity: 'error', summary: error.name, detail: error.message });
        }
      });
    }
    else{
      this.youtubeService.getPlaylistVideos(playlistId, token).subscribe({
        next: (res) => {
          if (isMore) {
            this.videos.update(curr => [...curr, ...res.items]);
          } else {
            this.videos.set(res.items);
          }
          
          this.videoNextToken = res.nextPageToken;
          this.isLoadingVideos.set(false);
          this.isLoadingMoreVideos.set(false);
        },
        error: (error) => {
          this.isLoadingVideos.set(false);
          this.isLoadingMoreVideos.set(false);
          this.messageService.add({ severity: 'error', summary: error.name, detail: error.message });
        }
      });
    }
  }

  // --- Video Infinite Scroll Setup ---
  private setupVideoScroll() {
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only load if we have a token, aren't loading, and have a playlist selected
        if (entry.isIntersecting && this.videoNextToken && !this.isLoadingMoreVideos() && this.selectedPlaylist()) {
          this.fetchVideos(this.selectedPlaylist()!.id, true);
        }
      });
    }, { root: null, threshold: 0.1 });

    if (this.videoScrollAnchor) {
      this.videoObserver.observe(this.videoScrollAnchor.nativeElement);
    }
  }

  // ==========================================
  // 3. Helpers
  // ==========================================
  
  openVideoUrl(videoId: string) {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString();
  }
}